import type { Money, Product, SellingPlan } from '@repo/shopify';
import { flattenConnection } from '@repo/shopify/client';
import {
	getDefaultSellingPlan,
	getSellingPlanFrequencyPhrase,
	getSellingPlanPercentOff,
	getSellingPlanUnitPrice,
	getSubscriptionPlans,
} from '@/lib/selling-plans';

/**
 * The Toolbox — Workman's tee-of-the-month membership. One Shopify product
 * ("Toolbox - The Apprentice", sized S–3XL) on a monthly Recharge plan; the
 * plan's price adjustment is the member price. The PDP offers it under the
 * add-to-cart button on every apparel page so a tee shopper can join, pick
 * their size and have the membership ride along in the same cart.
 *
 * Deliberately code constants rather than CMS fields while the offer is
 * being re-introduced; move them into product-page-settings once the copy
 * settles. The price, the sizes and the cadence all come from Shopify.
 */
export const TOOLBOX_MEMBERSHIP_HANDLE = 'the-toolbox';

/**
 * Master switch for the PDP row. Off until the membership's perks (10% off,
 * free shipping) are wired up for Toolbox subscribers and the row has been
 * reviewed on the live site; flipping this re-enables it on every apparel PDP.
 */
export const TOOLBOX_MEMBERSHIP_ENABLED = false;

export const TOOLBOX_MEMBERSHIP_TITLE = 'Toolbox membership';
/** The original storefront's benefits line, sentence-cased for the new buy box. */
export const TOOLBOX_MEMBERSHIP_BENEFITS = 'Monthly tees. 10% off. Free shipping. Early access.';
/** The same benefits spelled out, for the size sheet where there's room to read. */
export const TOOLBOX_MEMBERSHIP_DETAILS: readonly string[] = [
	'A new tee delivered every month',
	"Free shipping on every order while you're a member",
	'10% off your orders',
	'Early access to new releases',
];

export interface ToolboxMembershipVariant {
	id: string;
	/** The size option value, e.g. "M", "2XL". */
	size: string;
	sku: string | null;
	availableForSale: boolean;
}

export interface ToolboxMembershipOffer {
	productId: string;
	handle: string;
	title: string;
	vendor: string;
	productType: string;
	image: { url: string; altText: string | null; width?: number; height?: number } | null;
	/** The monthly plan the membership is added under. */
	plan: { id: string; name: string };
	/** "every month" — parsed from the plan, shown next to the member price. */
	frequencyPhrase: string;
	variants: ToolboxMembershipVariant[];
	/** The product's one-time price (struck through next to the member price). */
	oneTimePrice: Money;
	/** The per-delivery price on the plan. */
	memberPrice: Money;
	percentOff: number | null;
}

function getSizeValue(variant: {
	title: string;
	selectedOptions?: { name: string; value: string }[];
}) {
	const option = variant.selectedOptions?.find((entry) => /size/i.test(entry.name));
	return (option?.value ?? variant.title).trim();
}

/**
 * The offer for the buy box from the live Toolbox product, or null when it
 * can't be sold as a membership: no recurring plan, or nothing in stock. The
 * shopper-default plan rule is shared with every other subscription surface,
 * so a second Toolbox cadence would never be picked by accident.
 */
/** The slice of a product read the offer needs — the PDP's trimmed product type satisfies it too. */
export type ToolboxMembershipSource = Pick<
	Product,
	| 'id'
	| 'handle'
	| 'title'
	| 'vendor'
	| 'productType'
	| 'featuredImage'
	| 'variants'
	| 'sellingPlanGroups'
>;

export function buildToolboxMembershipOffer(
	product: ToolboxMembershipSource | null,
): ToolboxMembershipOffer | null {
	if (!product) return null;
	const plan: SellingPlan | null = getDefaultSellingPlan(getSubscriptionPlans(product));
	if (!plan) return null;

	const variants = flattenConnection(product.variants);
	const sized: ToolboxMembershipVariant[] = variants.map((variant) => ({
		id: variant.id,
		size: getSizeValue(variant),
		sku: variant.sku ?? null,
		availableForSale: variant.availableForSale,
	}));
	if (!sized.some((variant) => variant.availableForSale)) return null;

	const priced = variants.find((variant) => variant.availableForSale) ?? variants[0];
	if (!priced) return null;
	const image = product.featuredImage ?? priced.image ?? null;

	return {
		productId: product.id,
		handle: product.handle,
		title: product.title,
		vendor: product.vendor,
		productType: product.productType,
		image: image
			? { url: image.url, altText: image.altText ?? null, width: image.width, height: image.height }
			: null,
		plan: { id: plan.id, name: plan.name },
		frequencyPhrase: getSellingPlanFrequencyPhrase(plan) ?? 'every month',
		variants: sized,
		oneTimePrice: priced.price,
		memberPrice: getSellingPlanUnitPrice(priced.price, plan),
		percentOff: getSellingPlanPercentOff(priced.price, plan),
	};
}

/**
 * The size to pre-select: the size the shopper already picked for the tee
 * they're looking at when the Toolbox carries it in stock, else the first
 * in-stock size. Null only when nothing is in stock (the offer is hidden).
 */
export function pickToolboxSize(
	offer: Pick<ToolboxMembershipOffer, 'variants'>,
	preferredSize: string | null | undefined,
): string | null {
	const preferred = preferredSize?.trim().toLowerCase();
	const match = preferred
		? offer.variants.find(
				(variant) => variant.availableForSale && variant.size.toLowerCase() === preferred,
			)
		: undefined;
	return (match ?? offer.variants.find((variant) => variant.availableForSale))?.size ?? null;
}

/** "/mo" for a monthly plan, otherwise the plan's own phrase ("every 2 months"). */
export function formatMembershipCadence(frequencyPhrase: string): string {
	return frequencyPhrase === 'every month' ? '/mo' : ` ${frequencyPhrase}`;
}
